import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, Mail, Clock, RefreshCw, ArrowLeft, Users, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { logger } from '@/lib/logger';

const RESEND_COOLDOWN_SECONDS = 60;
const POLL_INTERVAL_MS = 5000;

/**
 * Écran dédié affiché lorsqu'un utilisateur a accepté une invitation
 * mais que son adresse email n'est pas encore confirmée.
 *
 * - Affiche clairement l'état "en attente de validation"
 * - Explique quand/comment l'accès sera activé
 * - Polling automatique : redirige vers le dashboard dès que la session
 *   devient active (après confirmation email + connexion)
 * - Permet de renvoyer l'email de confirmation
 */
const InvitationPending = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const email = searchParams.get('email') || '';
  const workspaceName = searchParams.get('workspace') || 'votre espace';

  const [isResending, setIsResending] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  usePageTitle(`Invitation acceptée — En attente de validation`);
  useDocumentHead({
    title: `Invitation acceptée — En attente de validation - Luminjo`,
    description: `Votre invitation à rejoindre ${workspaceName} est acceptée. Confirmez votre adresse email pour activer l'accès.`,
    noindex: true,
  });

  // Cooldown timer for resend button
  useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const timer = setInterval(() => setCooldownSeconds((s) => s - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldownSeconds]);

  // Poll for active session — once user confirms email & is logged in, redirect.
  useEffect(() => {
    let cancelled = false;

    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      if (data.session?.user) {
        toast({
          title: 'Accès activé !',
          description: `Bienvenue dans ${workspaceName}.`,
        });
        navigate('/', { replace: true });
      }
    };

    // Initial check
    checkSession();

    const interval = setInterval(checkSession, POLL_INTERVAL_MS);

    // Also listen to auth state changes for instant redirect
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      if (session?.user) {
        navigate('/', { replace: true });
      }
    });

    return () => {
      cancelled = true;
      clearInterval(interval);
      subscription.subscription.unsubscribe();
    };
  }, [navigate, toast, workspaceName]);

  const handleResend = async () => {
    if (!email || isResending || cooldownSeconds > 0) return;
    setIsResending(true);
    try {
      const { error } = await supabase.auth.resend({ type: 'signup', email });
      if (error) throw error;
      setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
      toast({
        title: 'Email renvoyé',
        description: 'Vérifiez votre boîte de réception.',
      });
    } catch (err) {
      logger.error('Failed to resend confirmation email', err, 'InvitationPending');
      toast({
        title: 'Erreur',
        description: "Impossible de renvoyer l'email pour l'instant. Réessayez plus tard.",
        variant: 'destructive',
      });
    } finally {
      setIsResending(false);
    }
  };

  const resendLabel = isResending
    ? 'Envoi en cours...'
    : cooldownSeconds > 0
      ? `Renvoyer dans ${cooldownSeconds}s`
      : "Renvoyer l'email de confirmation";

  return (
    <AuthLayout>
      <div className="space-y-8">
        {/* Status badge */}
        <div className="mx-auto w-24 h-24 rounded-full bg-success/10 flex items-center justify-center">
          <CheckCircle2 className="w-12 h-12 text-success" aria-hidden="true" />
        </div>

        <div className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-warning/10 text-warning text-xs font-medium">
            <Clock className="w-3.5 h-3.5" aria-hidden="true" />
            En attente de validation
          </div>
          <h1 className="text-3xl font-bold text-foreground">
            Invitation acceptée !
          </h1>
          <p className="text-muted-foreground">
            Vous avez bien rejoint l'équipe{' '}
            <span className="text-foreground font-medium">{workspaceName}</span>.
            Une dernière étape avant d'accéder à l'espace.
          </p>
        </div>

        {/* Required action */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-5 space-y-3">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-primary mt-0.5 shrink-0" aria-hidden="true" />
            <div className="space-y-1">
              <p className="font-medium text-foreground">
                Confirmez votre adresse email
              </p>
              <p className="text-sm text-muted-foreground">
                Nous avons envoyé un lien de confirmation
                {email ? (
                  <>
                    {' '}à <span className="text-foreground font-medium">{email}</span>
                  </>
                ) : (
                  ' à votre adresse'
                )}
                . Cliquez sur ce lien pour activer définitivement votre accès.
              </p>
            </div>
          </div>
        </div>

        {/* Timeline / what happens next */}
        <div className="space-y-3">
          <p className="text-sm font-medium text-foreground">Comment ça se passe ensuite :</p>
          <ol className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success/15 text-success text-xs font-semibold">
                1
              </span>
              <span className="text-sm text-muted-foreground pt-0.5">
                <span className="text-foreground font-medium">Invitation acceptée</span> — c'est fait.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary text-xs font-semibold">
                2
              </span>
              <span className="text-sm text-muted-foreground pt-0.5">
                <span className="text-foreground font-medium">Confirmez votre email</span> en cliquant sur le lien reçu (pensez aux dossiers <strong>spam</strong> et <strong>courrier indésirable</strong>).
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-semibold">
                3
              </span>
              <span className="text-sm text-muted-foreground pt-0.5">
                <span className="text-foreground font-medium">Accès activé automatiquement</span> — cette page vous redirigera dès la confirmation, et vous recevrez un email de bienvenue.
              </span>
            </li>
          </ol>
        </div>

        {/* Reassurance */}
        <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-4">
          <ShieldCheck className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" aria-hidden="true" />
          <p className="text-xs text-muted-foreground">
            Cette étape sécurise votre compte et garantit que seule la personne propriétaire de l'adresse{' '}
            {email ? <span className="font-medium text-foreground">{email}</span> : 'email'} peut accéder à l'espace.
          </p>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleResend}
            disabled={!email || isResending || cooldownSeconds > 0}
            aria-label="Renvoyer l'email de confirmation"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isResending ? 'animate-spin' : ''}`} aria-hidden="true" />
            {resendLabel}
          </Button>

          <Link
            to="/auth"
            className="flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Retour à la connexion
          </Link>
        </div>

        {/* Live polling hint */}
        <p className="text-center text-xs text-muted-foreground inline-flex items-center justify-center gap-2 w-full">
          <Users className="w-3.5 h-3.5" aria-hidden="true" />
          Vérification automatique en cours...
        </p>
      </div>
    </AuthLayout>
  );
};

export default InvitationPending;
